import React, { useEffect, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Grid, TextField, Button, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import { getOptionValue } from "common/enums";

import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { useLazyGetDcsAdditionalReport } from "apis/OtherBillingReport/otherBillingReport";
import { useLazyDownloadExcelQuery } from "slices/api";

const DcsBillingAdditionalReport = ({ t }) => {
  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const [
    getOtherBillingReports,
    {
      data: { data: billingReportsData } = {},
      isLoading: isBillingReportLoading,
      isFetching: isBillingReportFetching,
    },
  ] = useLazyGetDcsAdditionalReport();

  const sm = useMediaQuery("(max-width: 599px)");

  const [billCycleId, setBillCycleId] = useState("");

  const resetPageRef = useRef();

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType: currentSubscriptionType }) =>
                currentSubscriptionType ===
                getOptionValue("subscriptionTypes", "DCS")
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Sales Order Number",
        field: "salesOrderNumber",
      },
      {
        title: "FLAT/SHOP/ETC. NUMBER",
        field: "flatOrShopNumber",
      },

      { title: "Consumer Name", field: "consumerName" },
      {
        title: "Contract Demand",
        field: "contractDemand",
      },
      {
        title: "Billing Demand",
        field: "billingDemand",
      },
      {
        title: "Billing Demand in Excess of Contract Demand (Commercial)",
        field: "excessOfContractDemand",
      },
      {
        title: "BTU METER Serial NUMBER",
        field: "meterNumber",
      },
      {
        title: "Billing Period",
        field: "billingPeriod",
      },
      {
        title: "Previous reading (Tr-hr)",
        field: "previousReading",
      },
      {
        title: "Current reading (Tr-hr)",
        field: "currentReading",
      },
      {
        title: "Pro-Rata Consumption",
        field: "proRataConsumption",
      },
      {
        title: "Total Consumption (Tr-hr)",
        field: "totalConsumption",
      },
      {
        title: "Total Demand Charges",
        field: "totalDemandCharges",
      },
      {
        title: "Energy Charges",
        field: "energyCharges",
      },
      {
        title: "Fuel Surcharges",
        field: "fuelCharges",
      },
      {
        title: "Total Meter Charges (Residential)",
        field: "meterCharges",
      },
      {
        title: "Adjustment",
        field: "adjustment",
      },
      {
        title: "Total",
        field: "total",
      },
      {
        title: "9% SGST",
        field: "sgst",
      },
      {
        title: "9% CGST",
        field: "cgst",
      },
      {
        title: "18% IGST",
        field: "igst",
      },

      {
        title: "Total Bill",
        field: "totalBill",
      },
      {
        title: "Pending Dues",
        field: "pendingDues",
      },
    ],
    []
  );

  const downloadBtnHandler = async () => {
    try {
      if (billCycleId) {
        await downloadExcel({
          endpoint: "/report/dcs-additional-billing-report-excel",
          sheetName: `Dcs Additional ${
            updatedBillingCycleOptions.find(
              ({ value }) => value === billCycleId
            ).label
          }`,
          query: {
            billingCycleId: billCycleId,
          },
        });
      }
    } catch (error) {}
  };

  const getOtherUtilityReportsData = async (query) => {
    try {
      await getOtherBillingReports(query);
    } catch (error) {}
  };

  useEffect(() => {
    if (updatedBillingCycleOptions.length) {
      if (!billCycleId) {
        const billingCycleId =
          updatedBillingCycleOptions.find(
            ({ status }) => status !== getOptionValue("billingStatus", "Closed")
          )?.value ??
          updatedBillingCycleOptions.at(-1)?.value ??
          "";
        setBillCycleId(billingCycleId);
        getOtherUtilityReportsData({
          billingCycleId,
        });
      } else {
        getOtherUtilityReportsData({
          billingCycleId: billCycleId,
        });
      }
    }
  }, [updatedBillingCycleOptions, billCycleId]);

  return (
    <div className="pb-2">
      <Backdrop
        open={
          isBillingReportFetching ||
          isBillingReportLoading ||
          isExcelFileDownloading
        }
      />
      <BreadCrumb title={t("DCS Additional Report")} pageTitle={t("Billing")} />
      <Grid container columnSpacing={2} className="mb-1 mt-2">
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <SelectField
            onChange={(_, { value }) => setBillCycleId(value)}
            value={
              updatedBillingCycleOptions.find(
                (status) => status.value === billCycleId
              ) ?? ""
            }
            renderInput={(params) => {
              delete params.inputProps.id;
              delete params.id;
              return <TextField {...params} label={t("Select bill cycle")} />;
            }}
            options={updatedBillingCycleOptions}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadBtnHandler}
            disabled={!billCycleId}
          >
            {t("Download")}
          </Button>
        </Grid>
      </Grid>

      <BaseTable
        data={billingReportsData || []}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
        }}
        displaySrNo
        pageSize={10}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

DcsBillingAdditionalReport.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(DcsBillingAdditionalReport);
